import axios from 'axios';

// Default to port 5001 if env variable is not set
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/auth/password', { currentPassword, newPassword }),
  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string; avatar_url?: string }) =>
    api.put('/auth/profile', data),
  acceptGuidelines: () => api.post('/auth/accept-guidelines'),
};

// Users
export const usersApi = {
  getAll: (params?: { department?: string; role?: string; search?: string; mentor_id?: string; active?: boolean; page?: number; limit?: number }) =>
    api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string, options?: { permanent?: boolean; removeContributions?: boolean }) =>
    api.delete(`/users/${id}`, { params: options }),
  getTeam: () => api.get('/users/team'),
  getDepartments: () => api.get('/users/departments'),
  managePoints: (data: { user_id: string; amount: number; operation: 'add' | 'reduce'; reason?: string }) =>
    api.post('/users/manage-points', data),
  resetPassword: (id: string, new_password: string) =>
    api.put(`/users/${id}/reset-password`, { new_password }),
};

// Appreciations
export const appreciationsApi = {
  getFeed: (params?: { page?: number; department?: string; type?: string; days?: number; start_date?: string; end_date?: string }) =>
    api.get('/appreciations/feed', { params }),
  getMine: (params?: { direction?: 'received' | 'given'; page?: number }) =>
    api.get('/appreciations/mine', { params }),
  create: (data: {
    to_user_id: string;
    type: string;
    message: string;
    points: number;
    company_value_id?: string;
    is_public?: boolean;
    media_url?: string;
    media_type?: 'image' | 'video';
    media_files?: { url: string; type: string; name?: string }[];
    group_id?: string;
  }) => api.post('/appreciations', data),
  getComments: (id: string) =>
    api.get(`/appreciations/${id}/comments`),
  addComment: (id: string, content: string, parent_id?: string) =>
    api.post(`/appreciations/${id}/comments`, { content, parent_id }),
  addCommentReaction: (appreciationId: string, commentId: string, emoji: string) =>
    api.post(`/appreciations/${appreciationId}/comments/${commentId}/reactions`, { emoji }),
  addReaction: (id: string, emoji: string) =>
    api.post(`/appreciations/${id}/reactions`, { emoji }),
  getValues: () => api.get('/appreciations/values'),
  createValue: (data: { name: string; description: string; icon?: string; icon_url?: string; color?: string }) =>
    api.post('/appreciations/values', data),
  updateValue: (id: string, data: { name?: string; description?: string; icon?: string; icon_url?: string; color?: string }) =>
    api.put(`/appreciations/values/${id}`, data),
  deleteValue: (id: string) => api.delete(`/appreciations/values/${id}`),
  delete: (id: string) => api.delete(`/appreciations/${id}`),
  markViewed: (id: string) => api.post(`/appreciations/${id}/view`),
  getViewers: (id: string) => api.get(`/appreciations/${id}/viewers`),
  editComment: (appreciationId: string, commentId: string, content: string) =>
    api.put(`/appreciations/${appreciationId}/comments/${commentId}`, { content }),
  deleteComment: (appreciationId: string, commentId: string) =>
    api.delete(`/appreciations/${appreciationId}/comments/${commentId}`),
  update: (id: string, data: {
    message?: string;
    type?: string;
    company_value_id?: string | null;
    is_public?: boolean;
    media_url?: string | null;
    media_type?: 'image' | 'video' | null;
    media_files?: { url: string; type: string; name?: string }[];
  }) => api.put(`/appreciations/${id}`, data),
  addOn: (id: string, data: { points: number; message?: string }) =>
    api.post(`/appreciations/${id}/add-on`, data),
  getAddons: (id: string) =>
    api.get(`/appreciations/${id}/addons`),
};

// Announcements
export const announcementsApi = {
  getAll: (params?: { page?: number; pinned_only?: boolean; days?: number; start_date?: string; end_date?: string }) =>
    api.get('/announcements', { params }),
  getCelebrations: (params?: { month?: number; year?: number }) =>
    api.get('/announcements/celebrations', { params }),
  getById: (id: string) => api.get(`/announcements/${id}`),
  create: (data: {
    title: string;
    content: string;
    priority?: string;
    is_pinned?: boolean;
    target_roles?: string[];
    media_url?: string;
    media_type?: 'image' | 'video';
    media_files?: { url: string; type: string; name?: string }[];
    link_url?: string;
    link_title?: string;
  }) => api.post('/announcements', data),
  update: (id: string, data: any) => api.put(`/announcements/${id}`, data),
  delete: (id: string) => api.delete(`/announcements/${id}`),
  // Comments and reactions
  getComments: (id: string) => api.get(`/announcements/${id}/comments`),
  addComment: (id: string, content: string, parent_id?: string) => api.post(`/announcements/${id}/comments`, { content, parent_id }),
  sendCelebrationWaves: (id: string, data: { points: number; message?: string }) =>
    api.post(`/announcements/${id}/celebration-waves`, data),
  addCommentReaction: (announcementId: string, commentId: string, emoji: string) =>
    api.post(`/announcements/${announcementId}/comments/${commentId}/reactions`, { emoji }),
  editComment: (announcementId: string, commentId: string, content: string) => api.put(`/announcements/${announcementId}/comments/${commentId}`, { content }),
  deleteComment: (announcementId: string, commentId: string) => api.delete(`/announcements/${announcementId}/comments/${commentId}`),
  addReaction: (id: string, emoji: string) => api.post(`/announcements/${id}/reactions`, { emoji }),
  getReaders: (id: string) => api.get(`/announcements/${id}/readers`),
};

// Streams
export const streamsApi = {
  // Categories
  getCategories: () => api.get('/streams/categories'),
  createCategory: (data: { name: string; icon?: string; description?: string; display_order?: number }) =>
    api.post('/streams/categories', data),
  updateCategory: (id: string, data: any) => api.put(`/streams/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/streams/categories/${id}`),

  // Threads
  getThreads: (params?: { page?: number; category_id?: string; search?: string; days?: number; start_date?: string; end_date?: string }) =>
    api.get('/streams', { params }),
  getThreadById: (id: string) => api.get(`/streams/${id}`),
  createThread: (data: {
    category_id?: string;
    title: string;
    content: string;
    media_files?: { url: string; type: 'image' | 'video'; name?: string }[];
    link_url?: string;
    link_title?: string;
    poll?: { question: string; options: string[] };
  }) => api.post('/streams', data),
  updateThread: (id: string, data: any) => api.put(`/streams/${id}`, data),
  deleteThread: (id: string) => api.delete(`/streams/${id}`),
  getViewers: (id: string) => api.get(`/streams/${id}/viewers`),

  // Comments
  getComments: (id: string) => api.get(`/streams/${id}/comments`),
  addComment: (id: string, content: string, parent_id?: string) =>
    api.post(`/streams/${id}/comments`, { content, parent_id }),
  editComment: (threadId: string, commentId: string, content: string) =>
    api.put(`/streams/${threadId}/comments/${commentId}`, { content }),
  deleteComment: (threadId: string, commentId: string) =>
    api.delete(`/streams/${threadId}/comments/${commentId}`),

  // Reactions
  addReaction: (id: string, emoji: string) => api.post(`/streams/${id}/reactions`, { emoji }),
  addCommentReaction: (threadId: string, commentId: string, emoji: string) =>
    api.post(`/streams/${threadId}/comments/${commentId}/reactions`, { emoji }),

  // Poll votes
  votePoll: (threadId: string, option_id: string) =>
    api.post(`/streams/${threadId}/poll/vote`, { option_id }),
  unvotePoll: (threadId: string) =>
    api.delete(`/streams/${threadId}/poll/vote`),
  exportPollResults: (threadId: string) =>
    api.get(`/streams/${threadId}/poll/export`),
};

// Rewards
export const rewardsApi = {
  getAll: (params?: { category?: string; available_only?: boolean; page?: number; limit?: number; search?: string }) =>
    api.get('/rewards', { params }),
  getById: (id: string) => api.get(`/rewards/${id}`),
  create: (data: any) => api.post('/rewards', data),
  update: (id: string, data: any) => api.put(`/rewards/${id}`, data),
  delete: (id: string) => api.delete(`/rewards/${id}`),
  redeem: (id: string) => api.post(`/rewards/${id}/redeem`),
  getMyRedemptions: (params?: { page?: number }) =>
    api.get('/rewards/redemptions', { params }),
  getAllRedemptions: (params?: { status?: string; page?: number }) =>
    api.get('/rewards/redemptions/all', { params }),
  fulfillRedemption: (id: string, notes?: string) =>
    api.put(`/rewards/redemptions/${id}/fulfill`, { notes }),
  getCategories: () => api.get('/rewards/categories'),
};

// Analytics
export const analyticsApi = {
  getDashboard: (period?: number, startDate?: string, endDate?: string) =>
    api.get('/analytics/dashboard', { params: { period, startDate, endDate } }),
  getUserStats: (id?: string) =>
    api.get(id ? `/analytics/user/${id}` : '/analytics/user'),
  getTeamStats: (period?: number) =>
    api.get('/analytics/team', { params: { period } }),
  getLeaderboard: (params?: { period?: string; type?: string; limit?: number }) =>
    api.get('/analytics/leaderboard', { params }),
  getNotifications: (unread_only?: boolean) =>
    api.get('/analytics/notifications', { params: { unread_only } }),
  markNotificationRead: (id: string) =>
    api.put(`/analytics/notifications/${id}/read`),
  getWavesLedger: (userId?: string) =>
    api.get(userId ? `/analytics/waves-ledger/${userId}` : '/analytics/waves-ledger'),
};

// Events
export const eventsApi = {
  getAll: (params?: { month?: number; year?: number; include_auto?: boolean }) =>
    api.get('/events', { params }),
  getUpcoming: () =>
    api.get('/events/upcoming'),
  getByMonth: (year: number, month: number) =>
    api.get(`/events/month/${year}/${month}`),
  getEventImage: (id: string) =>
    api.get(`/events/${id}/image`),
  getTemplates: () =>
    api.get('/events/templates'),
  updateTemplate: (id: string, data: { title_template?: string; message_template?: string; image_url?: string }) =>
    api.put(`/events/templates/${id}`, data),
  createTemplate: (data: { template_type: string; title_template?: string; message_template?: string; image_url?: string; icon_name?: string }) =>
    api.post('/events/templates', data),
  deleteTemplate: (id: string) =>
    api.delete(`/events/templates/${id}`),
  create: (data: {
    title: string;
    event_date: string;
    description?: string;
    event_type?: string;
    image_url?: string;
    link_url?: string;
    link_title?: string;
    is_recurring?: boolean;
    recurring_frequency?: string;
    recurring_day_of_week?: number;
    recurring_week_of_month?: number;
    start_time?: string;
    end_time?: string;
  }) => api.post('/events', data),
  update: (id: string, data: {
    title?: string;
    event_date?: string;
    description?: string;
    event_type?: string;
    image_url?: string;
    link_url?: string;
    link_title?: string;
    is_recurring?: boolean;
    recurring_frequency?: string;
    recurring_day_of_week?: number;
    recurring_week_of_month?: number;
    start_time?: string;
    end_time?: string;
  }) => api.put(`/events/${id}`, data),
  delete: (id: string) =>
    api.delete(`/events/${id}`),
  updateUserDates: (userId: string, data: { birthday?: string; joining_date?: string }) =>
    api.put(`/events/user-dates/${userId}`, data),
  getAnnouncementTemplates: () =>
    api.get('/events/announcement-templates'),
  updateAnnouncementTemplate: (id: string, data: {
    title_template?: string;
    message_template?: string;
    image_url?: string;
    auto_post_enabled?: boolean;
    post_timing_days_before?: number;
  }) => api.put(`/events/announcement-templates/${id}`, data),
  createAnnouncementTemplate: (data: { template_type: string; title_template?: string; message_template?: string; image_url?: string; auto_post_enabled?: boolean; post_timing_days_before?: number }) =>
    api.post('/events/announcement-templates', data),
  deleteAnnouncementTemplate: (id: string) =>
    api.delete(`/events/announcement-templates/${id}`),
  getEventTypes: (includeInactive?: boolean) =>
    api.get('/events/types', { params: { include_inactive: includeInactive } }),
  createEventType: (data: { name: string; key: string; description?: string; icon_name?: string; color?: string }) =>
    api.post('/events/types', data),
  updateEventType: (id: string, data: { name?: string; description?: string; icon_name?: string; color?: string; is_active?: boolean; display_order?: number }) =>
    api.put(`/events/types/${id}`, data),
  deleteEventType: (id: string) =>
    api.delete(`/events/types/${id}`),
  reactToEvent: (id: string, reaction_type: string) =>
    api.post(`/events/${id}/react`, { reaction_type }),
  getEventReactions: (id: string) =>
    api.get(`/events/${id}/reactions`),
  getEventReactionDetails: (id: string) =>
    api.get(`/events/${id}/reaction-details`),

  // Wishes for synthetic birthday/anniversary events
  toggleEventWish: (targetUserId: string, eventType: 'birthday' | 'work_anniversary', year: number) =>
    api.post(`/events/wishes/${targetUserId}/${eventType}/${year}`),
  getEventWishes: (targetUserId: string, eventType: 'birthday' | 'work_anniversary', year: number) =>
    api.get(`/events/wishes/${targetUserId}/${eventType}/${year}`),
  getEventWishDetails: (targetUserId: string, eventType: 'birthday' | 'work_anniversary', year: number) =>
    api.get(`/events/wishes/${targetUserId}/${eventType}/${year}/details`),
};

// Xoxoday Integration
export const xoxodayApi = {
  // Health check
  getHealth: () => api.get('/xoxoday/health'),

  // User endpoints
  getMyBalance: () => api.get('/xoxoday/my-balance'),
  initiateSSO: () => api.post('/xoxoday/sso', {}),
  getRedemptionHistory: (params?: { page?: number; limit?: number }) =>
    api.get('/xoxoday/history', { params }),

  // Catalog endpoints
  getVouchers: (params?: {
    limit?: number;
    page?: number;
    country?: string;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    productName?: string;
    currencyCode?: string;
    includeProducts?: string;
    excludeProducts?: string;
  }) => api.get('/xoxoday/vouchers', { params }),
  getFilters: (params?: {
    filterGroupCode?: string;
    includeFilters?: string;
    excludeFilters?: string;
  }) => api.get('/xoxoday/filters', { params }),

  // Order endpoints (Direct API — Reward Points model)
  placeOrder: (data: {
    productId: number;
    quantity: number;
    denomination: number;
  }) => api.post('/xoxoday/place-order', data),
  getOrderDetails: (orderId: number) => api.get(`/xoxoday/order-details/${orderId}`),

  // Admin endpoints
  getConfig: () => api.get('/xoxoday/admin/config'),
  updateTokens: (data: {
    client_id?: string;
    client_secret?: string;
    access_token?: string;
    refresh_token?: string;
  }) => api.put('/xoxoday/admin/tokens', data),
  getAuditLogs: (params?: { action?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/xoxoday/admin/logs', { params }),
  getOrderHistory: (params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    page?: number;
  }) => api.get('/xoxoday/admin/order-history', { params }),
};

// Auto Waves API (Superadmin only)
export const autoWavesApi = {
  // Rules
  getRules: (includeInactive?: boolean) =>
    api.get('/auto-waves/rules', { params: { includeInactive } }),
  getRuleById: (id: string) => api.get(`/auto-waves/rules/${id}`),
  createRule: (data: {
    name: string;
    description?: string;
    rule_type: 'recurring' | 'birthday' | 'work_anniversary';
    frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    day_of_week?: number;
    day_of_month?: number;
    month_of_year?: number;
    waves_amount: number;
    target_type?: 'all_active' | 'by_role' | 'specific_users';
    target_roles?: string[];
    target_user_ids?: string[];
    reason_template?: string;
    is_active?: boolean;
  }) => api.post('/auto-waves/rules', data),
  updateRule: (id: string, data: {
    name?: string;
    description?: string;
    rule_type?: 'recurring' | 'birthday' | 'work_anniversary';
    frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    day_of_week?: number;
    day_of_month?: number;
    month_of_year?: number;
    waves_amount?: number;
    target_type?: 'all_active' | 'by_role' | 'specific_users';
    target_roles?: string[];
    target_user_ids?: string[];
    reason_template?: string;
    is_active?: boolean;
  }) => api.put(`/auto-waves/rules/${id}`, data),
  deleteRule: (id: string) => api.delete(`/auto-waves/rules/${id}`),

  // Logs
  getLogs: (params?: { ruleId?: string; limit?: number }) =>
    api.get('/auto-waves/logs', { params }),

  // Actions
  triggerRule: (id: string) => api.post(`/auto-waves/rules/${id}/trigger`),
  runScheduler: () => api.post('/auto-waves/run-scheduler'),
};

// Integrations API (News, Quotes, etc.)
export const integrationsApi = {
  // Get all settings
  getAllSettings: () => api.get('/integrations/settings'),

  // Get settings by type
  getSettingsByType: (type: string) => api.get(`/integrations/settings/${type}`),

  // Save settings
  saveSettings: (data: {
    integration_type: string;
    provider: string;
    api_key?: string;
    category?: string;
    is_enabled?: boolean;
    config?: Record<string, any>;
  }) => api.post('/integrations/settings', data),

  // Test connection
  testConnection: (data: {
    integration_type: string;
    provider: string;
    api_key?: string;
  }) => api.post('/integrations/test', data),

  // Delete all settings for a type
  deleteSettings: (type: string) => api.delete(`/integrations/settings/${type}`),

  // Delete a specific source by type and provider
  deleteSettingsByProvider: (type: string, provider: string) =>
    api.delete(`/integrations/settings/${type}/${encodeURIComponent(provider)}`),
};

// Settings - Ripple Types & Role Permissions
export const settingsApi = {
  // Ripple Types
  getRippleTypes: () => api.get('/settings/ripple-types'),
  createRippleType: (data: {
    name: string;
    key: string;
    description?: string;
    icon?: string;
    color?: string;
    default_points?: number;
  }) => api.post('/settings/ripple-types', data),
  updateRippleType: (id: string, data: {
    name?: string;
    key?: string;
    description?: string;
    icon?: string;
    color?: string;
    default_points?: number;
    is_active?: boolean;
    display_order?: number;
  }) => api.put(`/settings/ripple-types/${id}`, data),
  deleteRippleType: (id: string) => api.delete(`/settings/ripple-types/${id}`),

  // Features
  getFeatures: () => api.get('/settings/features'),
  createFeature: (data: {
    name: string;
    key: string;
    description?: string;
    category: string;
  }) => api.post('/settings/features', data),

  // Role Permissions
  getRolePermissions: (role?: string) =>
    api.get('/settings/role-permissions', { params: role ? { role } : {} }),
  getPermissionsMatrix: () => api.get('/settings/permissions-matrix'),
  updateRolePermission: (data: {
    feature_id: string;
    role: string;
    can_view?: boolean;
    can_create?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
  }) => api.put('/settings/role-permissions', data),
  bulkUpdateRolePermissions: (permissions: Array<{
    feature_id: string;
    role: string;
    can_view?: boolean;
    can_create?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
  }>) => api.put('/settings/role-permissions/bulk', { permissions }),

  // Security Audit
  runSecurityAudit: () => api.post('/settings/security-audit'),
};

export default api;
